import type {
  CarpoolAssignment,
  CarpoolDepartureType,
  CarpoolJourneyType,
  CarpoolResponse,
  CarpoolResponseStatus,
  Id,
} from './types.ts';
import { CARPOOL_DEPARTURE_TYPE_LABELS } from './types.ts';

/** Affiche le point de départ d'une voiture (locaux ou lieu libre). */
export function carpoolDepartureLabel(
  departureType: CarpoolDepartureType | null | undefined,
  customLocation: string | null | undefined,
): string {
  if (departureType === 'locaux') return CARPOOL_DEPARTURE_TYPE_LABELS.locaux;
  if (departureType === 'autre') {
    const custom = customLocation?.trim();
    return custom || CARPOOL_DEPARTURE_TYPE_LABELS.autre;
  }
  return 'Point de départ inconnu';
}

/** `true` si le statut de réponse couvre ce trajet. */
export function carpoolNeedsJourney(
  status: CarpoolResponseStatus,
  journey: CarpoolJourneyType,
): boolean {
  if (status === 'aucun') return false;
  if (status === 'aller_retour') return true;
  if (journey === 'aller') return status === 'aller_seulement';
  return status === 'retour_seulement';
}

export interface CarpoolCar {
  driverParentId: Id;
  /** Places libres pour les enfants des autres familles. */
  capacity: number;
  departureType: CarpoolDepartureType | null;
  customLocation: string | null;
  /** Enfants du conducteur qui font ce trajet. */
  ownChildIds: Id[];
}

export interface CarpoolAssignmentDraft {
  journeyType: CarpoolJourneyType;
  driverParentId: Id;
  passengerBaladinId: Id;
}

/**
 * Construit les voitures d'un trajet à partir des réponses parents.
 * Une voiture = un parent qui offre au moins une place sur ce trajet.
 * Capacité = max des places déclarées (même voiture, plusieurs fiches enfants).
 */
export function buildCarpoolCars(
  responses: CarpoolResponse[],
  journey: CarpoolJourneyType,
  childrenByParent: Map<Id, Id[]>,
): CarpoolCar[] {
  const byParent = new Map<
    Id,
    { capacity: number; departureType: CarpoolDepartureType | null; customLocation: string | null }
  >();

  for (const response of responses) {
    const seats = journey === 'aller' ? response.seatsAller : response.seatsRetour;
    if (seats <= 0) continue;
    const current = byParent.get(response.parentId);
    if (!current || seats > current.capacity) {
      byParent.set(response.parentId, {
        capacity: seats,
        departureType: response.departureType,
        customLocation: response.customLocation,
      });
    }
  }

  const cars: CarpoolCar[] = [];
  for (const [driverParentId, info] of byParent) {
    const ownChildIds = (childrenByParent.get(driverParentId) ?? []).filter((baladinId) => {
      const response = responses.find((row) => row.baladinId === baladinId);
      return response ? carpoolNeedsJourney(response.status, journey) : false;
    });
    cars.push({
      driverParentId,
      capacity: info.capacity,
      departureType: info.departureType,
      customLocation: info.customLocation,
      ownChildIds,
    });
  }

  return cars.sort((a, b) =>
    carpoolDepartureLabel(a.departureType, a.customLocation).localeCompare(
      carpoolDepartureLabel(b.departureType, b.customLocation),
      'fr',
    ),
  );
}

/** Enfants qui ont besoin d'une place sur ce trajet. */
export function carpoolRidersForJourney(
  responses: CarpoolResponse[],
  journey: CarpoolJourneyType,
): Id[] {
  return responses
    .filter((response) => carpoolNeedsJourney(response.status, journey))
    .map((response) => response.baladinId);
}

function externalPassengers(car: CarpoolCar, assigned: Id[]): Id[] {
  return assigned.filter((baladinId) => !car.ownChildIds.includes(baladinId));
}

export function carpoolRemainingSeats(car: CarpoolCar, assignedPassengerIds: Id[]): number {
  return Math.max(0, car.capacity - externalPassengers(car, assignedPassengerIds).length);
}

/**
 * Répartit automatiquement les enfants sans place dans les voitures
 * ayant encore de la capacité. Les enfants du conducteur sont placés
 * dans sa voiture sans consommer de place offerte.
 */
export function autoAssignCarpool(
  responses: CarpoolResponse[],
  journey: CarpoolJourneyType,
  childrenByParent: Map<Id, Id[]>,
): CarpoolAssignmentDraft[] {
  const cars = buildCarpoolCars(responses, journey, childrenByParent);
  const riders = new Set(carpoolRidersForJourney(responses, journey));
  const assignments: CarpoolAssignmentDraft[] = [];
  const assigned = new Set<Id>();

  // 1. Enfants du conducteur → sa voiture.
  for (const car of cars) {
    for (const baladinId of car.ownChildIds) {
      if (!riders.has(baladinId) || assigned.has(baladinId)) continue;
      assignments.push({
        journeyType: journey,
        driverParentId: car.driverParentId,
        passengerBaladinId: baladinId,
      });
      assigned.add(baladinId);
    }
  }

  // 2. Autres enfants → premières voitures avec places libres.
  const waiting = [...riders]
    .filter((baladinId) => !assigned.has(baladinId))
    .sort((a, b) => a.localeCompare(b));

  const seatsLeft = new Map(cars.map((car) => [car.driverParentId, car.capacity]));

  for (const baladinId of waiting) {
    const car = cars.find((entry) => (seatsLeft.get(entry.driverParentId) ?? 0) > 0);
    if (!car) break;
    assignments.push({
      journeyType: journey,
      driverParentId: car.driverParentId,
      passengerBaladinId: baladinId,
    });
    assigned.add(baladinId);
    seatsLeft.set(car.driverParentId, (seatsLeft.get(car.driverParentId) ?? 0) - 1);
  }

  return assignments;
}

/** Répartition auto aller + retour. */
export function autoAssignCarpoolBoth(
  responses: CarpoolResponse[],
  childrenByParent: Map<Id, Id[]>,
): CarpoolAssignmentDraft[] {
  return [
    ...autoAssignCarpool(responses, 'aller', childrenByParent),
    ...autoAssignCarpool(responses, 'retour', childrenByParent),
  ];
}

/** Passagers déjà placés pour une voiture / trajet. */
export function passengersInCar(
  assignments: Array<Pick<CarpoolAssignment, 'journeyType' | 'driverParentId' | 'passengerBaladinId'>>,
  journey: CarpoolJourneyType,
  driverParentId: Id,
): Id[] {
  return assignments
    .filter((row) => row.journeyType === journey && row.driverParentId === driverParentId)
    .map((row) => row.passengerBaladinId);
}

/** Enfants encore sans place pour ce trajet. */
export function carpoolWaitingList(
  responses: CarpoolResponse[],
  assignments: Array<Pick<CarpoolAssignment, 'journeyType' | 'passengerBaladinId'>>,
  journey: CarpoolJourneyType,
): Id[] {
  const riders = carpoolRidersForJourney(responses, journey);
  const placed = new Set(
    assignments.filter((row) => row.journeyType === journey).map((row) => row.passengerBaladinId),
  );
  return riders.filter((baladinId) => !placed.has(baladinId));
}
